import { useCart } from '../contexts/CartContext';
import { Link } from 'react-router-dom';

export default function Cart() {
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount, isCartOpen, closeCart } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-ink/40 z-40 transition-opacity"
        onClick={closeCart}
      />

      {/* Cart Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-cream-50 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cream-300">
          <h2 className="font-serif text-3xl font-semibold text-ink">
            Your Cart ({getCartCount()})
          </h2>
          <button
            onClick={closeCart}
            className="text-ink-muted hover:text-ink transition-colors" aria-label="Close cart"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-20 h-20 mx-auto text-cream-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <h3 className="font-serif text-2xl text-ink mb-2">Your cart is empty</h3>
              <p className="text-ink-soft mb-6">Add a pair of handmade earrings to get started.</p>
              <Link
                to="/shop"
                onClick={closeCart}
                className="btn-brand"
              >
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.id} className="flex gap-4 bg-white border border-cream-300 rounded-xl p-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-cream-200 rounded-lg overflow-hidden">
                    {item.photoUrl ? (
                      <img
                        src={item.photoUrl}
                        alt={item.description || item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-ink-muted text-3xl">
                        📷
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-ink capitalize truncate">{item.description || item.name}</h3>
                    <p className="text-xs text-ink-muted truncate">SKU: {item.sku}</p>
                    <p className="font-medium text-sage-700 mt-1">
                      ${item.price.toFixed(2)}
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-cream-300 rounded-full overflow-hidden">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="px-3 py-1 text-ink-soft hover:bg-cream-200 transition-colors"
                        >
                          −
                        </button>
                        <span className="px-3 py-1 text-sm font-medium text-ink">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="px-3 py-1 text-ink-soft hover:bg-cream-200 transition-colors"
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-ink-muted hover:text-red-700 text-sm underline underline-offset-4 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {cart.length > 0 && (
          <div className="border-t border-cream-300 p-6 bg-cream-100">
            <div className="flex justify-between items-center mb-4">
              <span className="text-ink-soft">Subtotal</span>
              <span className="font-serif text-3xl font-semibold text-ink">
                ${getCartTotal().toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-ink-muted mb-5">
              Shipping and taxes calculated at checkout
            </p>
            <Link
              to="/checkout"
              onClick={closeCart}
              className="btn-brand w-full py-4"
            >
              Proceed to Checkout
            </Link>
            <button
              onClick={closeCart}
              className="block w-full mt-3 text-sage-700 text-center py-2 text-sm hover:text-sage-800 transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
